import { Injectable } from '@angular/core';
import { trustedScriptUrl } from '@app/core/security/trusted-types';

interface ScriptLoadOptions {
  id: string;
  src: string;
  async?: boolean;
  defer?: boolean;
  attrs?: Record<string, string>;
  target?: 'head' | 'body';
}

@Injectable({ providedIn: 'root' })
export class ExternalScriptLoaderService {
  private promises = new Map<string, Promise<void>>();

  load(options: ScriptLoadOptions): Promise<void> {
    if (typeof document === 'undefined') return Promise.resolve();

    const existing = document.getElementById(options.id) as HTMLScriptElement | null;
    if (existing?.dataset['loaded'] === 'true') return Promise.resolve();

    const key = options.id || options.src;
    const pending = this.promises.get(key);
    if (pending) return pending;

    const promise = new Promise<void>((resolve, reject) => {
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${options.src}`)), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = options.id;
      script.src = trustedScriptUrl(options.src) as string;
      script.async = options.async ?? true;
      script.defer = options.defer ?? false;

      for (const [name, value] of Object.entries(options.attrs ?? {})) {
        script.setAttribute(name, value);
      }

      script.onload = () => {
        script.dataset['loaded'] = 'true';
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load ${options.src}`));

      document[options.target ?? 'head'].appendChild(script);
    }).finally(() => this.promises.delete(key));

    this.promises.set(key, promise);
    return promise;
  }
}
